// Sales Cabinet — root component + routing.
//
// Auth guard: protected routes all go through <AppShell>, which redirects
// to /login if no token is in localStorage. The login page is unprotected.

import { useEffect, useState } from "react";
import { Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
import { LoginPage } from "./pages/LoginPage";
import { DashboardPage } from "./pages/DashboardPage";
import { PartnersPage } from "./pages/PartnersPage";
import { UsersPage } from "./pages/UsersPage";
import { AuditLogPage } from "./pages/AuditLogPage";
import { MonitoringPage } from "./pages/MonitoringPage";
import { ServiceTokensPage } from "./pages/ServiceTokensPage";
import { DevicesPage } from "./pages/DevicesPage";
import { MigrationPage } from "./pages/MigrationPage";
import { AppUpdatePage } from "./pages/AppUpdatePage";
import { SettingsPage } from "./pages/SettingsPage";
import { PhonesPage } from "./pages/PhonesPage";
import { BillingPage } from "./pages/BillingPage";
import { FirmwarePage } from "./pages/FirmwarePage";
import { loadFirmwareAccess } from "./lib/firmware";
import { getUser, isAuthed, logout } from "./lib/auth";

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/" element={<Shell />}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="partners" element={<PartnersPage />} />
        <Route path="devices" element={<DevicesPage />} />
        <Route path="migration" element={<MigrationPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="audit" element={<AuditLogPage />} />
        <Route path="monitoring" element={<MonitoringPage />} />
        <Route path="service-tokens" element={<ServiceTokensPage />} />
        <Route path="app-update" element={<AppUpdatePage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="phones" element={<PhonesPage />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="firmware" element={<FirmwarePage />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
  );
}

function Shell() {
  const location = useLocation();
  const [authed, setAuthed] = useState(isAuthed());
  const user = getUser();
  // Пункт «Прошивки» — только учётке с правом на них.
  const [firmwareAccess, setFirmwareAccess] = useState(false);
  useEffect(() => {
    if (authed) loadFirmwareAccess().then(setFirmwareAccess);
  }, [authed]);

  // Re-check auth on route change — handles the case where token is cleared
  // mid-session (e.g., 401 from GraphQL).
  useEffect(() => {
    setAuthed(isAuthed());
  }, [location]);

  if (!authed) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="app-layout">
      <nav className="sidebar">
        <h1>Evimiz Sales</h1>
        <NavLink to="/dashboard">Дашборд</NavLink>
        <NavLink to="/partners">Партнёры</NavLink>
        <NavLink to="/billing">Биллинг</NavLink>
        <NavLink to="/phones">Телефоны</NavLink>
        <NavLink to="/devices">Устройства</NavLink>
        {firmwareAccess && <NavLink to="/firmware">Прошивки</NavLink>}
        <NavLink to="/migration">Перенос с Асгарда</NavLink>
        <NavLink to="/users">Пользователи</NavLink>
        <NavLink to="/audit">Аудит</NavLink>
        <NavLink to="/monitoring">Мониторинг</NavLink>
        <NavLink to="/service-tokens">Токены</NavLink>
        <NavLink to="/app-update">Обновление</NavLink>
        <NavLink to="/settings">Юр-документы</NavLink>
        <div className="spacer" />
        <div className="user">
          {user?.email}
          <br />
          <button
            className="secondary"
            style={{ marginTop: 6, width: "100%", padding: "4px 8px", fontSize: 12 }}
            onClick={logout}
          >
            Выйти
          </button>
        </div>
      </nav>
      <main className="content">
        <RouterOutlet />
      </main>
    </div>
  );
}

// react-router v7 exposes <Outlet/> but we prefer an explicit wrapper so
// the sidebar layout stays a single source of truth.
import { Outlet } from "react-router-dom";
function RouterOutlet() {
  return <Outlet />;
}

function NavLink({ to, children }: { to: string; children: React.ReactNode }) {
  const location = useLocation();
  const active = location.pathname.startsWith(to);
  return (
    <Link to={to} className={active ? "active" : ""}>
      {children}
    </Link>
  );
}
